/* Supply › Reports › Stock › Out of Stuck Report
 * Orders stuck due to stock shortage, with async CSV generation
 * (task → poll → download). Data: /pharmacy-ims/api/v1/reports/out-of-stock. */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTaskCenter } from "../../Layout/TaskCenterContext";
import { useWarehouse } from "../../Layout/WarehouseContext";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateTimeRangeFilter,
  KpiBox,
  LoadingState,
  ErrorState,
  IconDownload,
  IconChevRight,
} from "../supplyKit";
import { ReportBack } from "./reportShared";
import outOfStockService, { last7DaysRange, toDateTimeRange } from "./outOfStockService";
import { PHARMACY_ROUTES } from "../../../Route/path";

/* "2026-06-19T13:07:48.667538" -> "2026-06-19 13:07". */
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16).replace("T", " ");
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const POLL_INTERVAL = 1500;
const POLL_MAX_ATTEMPTS = 40; // ~60s before timing out

/* No warehouse in filter state — it comes from the app-wide selection and is
   merged in at query time (toApiFilters), so it cannot go stale here. */
const defaultFilters = () => ({
  status: "ALL",
  ...last7DaysRange(),
  search: "",
  sort: "ASC", // oldest first
});

/* Filter state -> API query params (warehouse/status/search/sort + ISO range). */
const toApiFilters = (f, warehouseId) => ({
  warehouseId,
  status: f.status,
  search: f.search,
  sort: f.sort,
  ...toDateTimeRange(f),
});

const OutOfStuckReport = () => {
  const { runTask } = useTaskCenter();
  /* The store in scope, chosen once in the topbar. */
  const { code: wh } = useWarehouse();
  const [filters, setFilters] = useState(defaultFilters);
  const setF = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  /* Debounced copy of `filters` that actually drives the fetch. */
  const [applied, setApplied] = useState(filters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (key, val) =>
    setPageState((s) => ({ ...s, [key]: val, ...(key === "size" ? { page: 0 } : {}) }));

  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  /* Debounce filter edits → applied (text inputs would otherwise fetch per keystroke). */
  useEffect(() => {
    const t = setTimeout(() => setApplied(filters), 400);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [applied]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    outOfStockService
      .list(toApiFilters(applied, wh), { page: pageState.page, size: pageState.size })
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Could not load Out of Stuck report.";
        setError(msg);
        toast.error(msg);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [applied, wh, pageState.page, pageState.size, reloadKey]);

  /* Kick off the async CSV via the global task center so it keeps running (and
     shows progress in the topbar bell) even if the user navigates away. */
  const startExport = () => {
    if (submitting) return;
    setSubmitting(true);
    const filters = toApiFilters(applied, wh);
    runTask({
      title: "Out of Stuck Report · CSV",
      run: async (onProgress) => {
        onProgress("Requesting…");
        const task = await outOfStockService.requestExport(filters);
        if (!task?.taskId) throw new Error("Export did not return a task id.");
        const fileName = task.fileName || `out-of-stuck-${filters.warehouseId || "all"}.csv`;
        const doDownload = () => outOfStockService.download(task.taskId, fileName);

        let cur = task;
        for (let i = 0; cur.status === "PENDING" && i < POLL_MAX_ATTEMPTS; i++) {
          onProgress("Generating report…");
          await sleep(POLL_INTERVAL);
          cur = await outOfStockService.exportStatus(task.taskId);
        }
        if (cur.status === "FAILED") throw new Error(cur.message || "Report generation failed.");
        if (cur.status !== "READY") throw new Error("Report generation timed out. Please try again.");

        onProgress("Downloading…");
        await doDownload();
        return {
          detail: `Downloaded${cur.rowCount != null ? ` · ${cbFmtNum(cur.rowCount)} rows` : ""}`,
          action: { label: "Download again", run: doDownload },
        };
      },
    });
    toast.info("Generating report — track progress in notifications.");
    // brief guard against double-submit; the task itself lives in the task center
    setTimeout(() => setSubmitting(false), 1200);
  };

  const totalShort = rows.reduce((s, r) => s + r.shortageQty, 0);
  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ReportBack to={PHARMACY_ROUTES.STOCK_REPORT} label="Stock Reports" />
      <SectionHeader
        title="Out of Stuck Report"
        sub="Orders stuck due to stock shortage · generate a CSV to download"
        actions={
          <Button variant="primary" icon={<IconDownload size={14} />} onClick={startExport} disabled={submitting}>
            Generate CSV
          </Button>
        }
      />

      <ToolbarBar>
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="ALL">All</option>
          <option value="CREATED">CREATED</option>
          <option value="CLOSED">CLOSED</option>
        </Select>
        <DateTimeRangeFilter
          label="Order date"
          from={filters.fromDate}
          to={filters.toDate}
          fromTime={filters.fromTime}
          toTime={filters.toTime}
          onApply={({ from, to, fromTime, toTime }) =>
            setFilters((f) => ({ ...f, fromDate: from, toDate: to, fromTime, toTime }))
          }
        />
        <Select value={filters.sort} onChange={(e) => setF("sort", e.target.value)}>
          <option value="ASC">Oldest first</option>
          <option value="DESC">Newest first</option>
        </Select>
        <Input
          value={filters.search}
          onChange={(e) => setF("search", e.target.value)}
          placeholder="Order ID or patient ID…"
          style={{ width: 220, maxWidth: "100%" }}
        />
        <Button onClick={() => setFilters(defaultFilters())}>Reset</Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} stuck orders
        </div>
      </ToolbarBar>

      <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: "grid", gap: 10 }}>
        <KpiBox label="Stuck orders" value={cbFmtNum(pageState.totalElements)} variant="red" />
        <KpiBox label="Short units (this page)" value={cbFmtNum(totalShort)} variant="amber" />
      </div>

      {loading ? (
        <LoadingState label="Loading Out of Stuck report…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <>
          <Table
            columns={[
              { label: "", width: 24 },
              { label: "Order ID" },
              { label: "Patient" },
              { label: "Channel" },
              { label: "Items", align: "right" },
              { label: "Total MRP", align: "right" },
              { label: "Paid", align: "right" },
              { label: "Short", align: "right" },
              { label: "Created" },
              { label: "Status" },
            ]}
          >
            {rows.length === 0 && (
              <tr>
                <Td colSpan={10} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                  No stuck orders match these filters.
                </Td>
              </tr>
            )}
            {rows.map((r) => {
              const isOpen = expanded.has(r.id);
              return (
                <React.Fragment key={r.id}>
                  <Tr selected={isOpen} onClick={() => toggleRow(r.id)}>
                    <Td>
                      <IconChevRight
                        size={12}
                        style={{
                          transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                          transition: "transform 140ms",
                          color: "var(--fg-muted)",
                        }}
                      />
                    </Td>
                    <Td mono style={{ fontWeight: 600 }}>
                      {r.orderId}
                    </Td>
                    <Td mono>{r.patientId || <span style={{ color: "var(--fg-muted)" }}>—</span>}</Td>
                    <Td>
                      <Pill variant="neutral">{r.sourceChannel || "—"}</Pill>
                    </Td>
                    <Td mono right>
                      {cbFmtNum(r.items.length)}
                    </Td>
                    <Td mono right>
                      {cbFmtINR(r.totalMrp)}
                    </Td>
                    <Td mono right>
                      {cbFmtINR(r.paidAmount)}
                    </Td>
                    <Td mono right style={{ fontWeight: 600, color: "var(--red-700)" }}>
                      {cbFmtNum(r.shortageQty)}
                    </Td>
                    <Td mono>{fmtDateTime(r.createdAt)}</Td>
                    <Td>
                      <Pill variant={r.status === "CLOSED" ? "green" : "amber"} dot>
                        {r.status || "—"}
                      </Pill>
                    </Td>
                  </Tr>
                  {isOpen && (
                    <Tr style={{ background: "var(--bg-sunken)" }}>
                      <Td />
                      <Td colSpan={9} style={{ padding: "8px 12px" }}>
                        {r.stuckReason && (
                          <div style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
                            {r.stuckReason}
                          </div>
                        )}
                        {r.items.length === 0 ? (
                          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>No line-item details for this order.</div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {r.items.map((it) => (
                              <div
                                key={it.itemId ?? `${it.drugCode}`}
                                style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}
                              >
                                <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)", minWidth: 90 }}>
                                  {it.drugCode}
                                </span>
                                <span style={{ fontWeight: 600, color: "var(--fg-strong)", flex: 1 }}>{it.drugName}</span>
                                <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>
                                  Ordered {cbFmtNum(it.quantity)}
                                </span>
                                <span style={{ fontFamily: "var(--font-mono)", color: "var(--fg-muted)" }}>
                                  Avail {cbFmtNum(it.availableQty)}
                                </span>
                                <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--red-700)" }}>
                                  Short {cbFmtNum(it.shortageQty)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table>

          <ToolbarBar>
            <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
              {[20, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </Select>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {start}–{end} of {cbFmtNum(pageState.totalElements)}
              </span>
              <Button
                size="sm"
                disabled={pageState.page <= 0}
                onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}
              >
                ← Prev
              </Button>
              <Button
                size="sm"
                disabled={pageState.page + 1 >= pageState.totalPages}
                onClick={() => setPageField("page", pageState.page + 1)}
              >
                Next →
              </Button>
            </div>
          </ToolbarBar>
        </>
      )}
    </div>
  );
};

export default OutOfStuckReport;
